/**
 * Customer-signature canvas.
 *
 * Wraps the `signature_pad` library in a React component. Manages canvas
 * resize (high-DPR safe), exposes Clear + Done buttons, and surfaces the
 * resulting PNG to the parent via onCapture.
 *
 * The data URL has the form `data:image/png;base64,<payload>`. The PWA strips
 * the prefix before sending to the API (which expects raw base64).
 */

import SignaturePad from 'signature_pad';
import { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button.js';

export interface CapturedSignature {
  contentType: 'image/png';
  /** Raw base64 (no `data:` prefix). */
  base64: string;
}

interface SignaturePadComponentProps {
  onCapture: (signature: CapturedSignature) => void;
  onClear: () => void;
  /** Optional initial PNG (data URL) to render when reopening a saved signature. */
  initial?: string | null;
}

export function SignaturePadComponent({ onCapture, onClear, initial }: SignaturePadComponentProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const padRef = useRef<SignaturePad | null>(null);
  const [empty, setEmpty] = useState(!initial);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeCanvas = () => {
      const ratio = Math.max(window.devicePixelRatio ?? 1, 1);
      const { offsetWidth, offsetHeight } = canvas;
      // Preserve any existing strokes across resize.
      const existing = padRef.current?.toData();
      canvas.width = offsetWidth * ratio;
      canvas.height = offsetHeight * ratio;
      const ctx = canvas.getContext('2d');
      ctx?.scale(ratio, ratio);
      padRef.current?.clear();
      if (existing && existing.length > 0) padRef.current?.fromData(existing);
    };

    const pad = new SignaturePad(canvas, {
      // Opaque white, NOT transparent: the PNG lands as a Spire CRM-note
      // attachment, and viewers that composite on dark backgrounds render
      // a transparent signature invisible (audit P1). Dark ink on white
      // survives any viewer.
      backgroundColor: '#FFFFFF',
      penColor: '#111827',
    });
    padRef.current = pad;

    pad.addEventListener('endStroke', () => setEmpty(pad.isEmpty()));

    resizeCanvas();
    if (initial) {
      const img = new Image();
      img.onload = () => {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          setEmpty(false);
        }
      };
      img.src = initial;
    }

    window.addEventListener('resize', resizeCanvas);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      pad.off();
      padRef.current = null;
    };
  }, [initial]);

  const clear = () => {
    padRef.current?.clear();
    setEmpty(true);
    onClear();
  };

  const capture = () => {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) return;
    const dataUrl = pad.toDataURL('image/png');
    const base64 = dataUrl.replace(/^data:image\/png;base64,/, '');
    onCapture({ contentType: 'image/png', base64 });
  };

  return (
    <div className="space-y-2">
      <div className="relative w-full" style={{ aspectRatio: '5 / 2' }}>
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full bg-surface border-2 border-dashed rounded-lg touch-none"
          style={{ borderColor: 'var(--color-border-secondary)' }}
          aria-label="Customer signature canvas"
        />
        {empty && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-ink-subtle text-sm">
            Sign here
          </div>
        )}
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" size="sm" variant="ghost" onClick={clear}>
          Clear
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={capture} disabled={empty}>
          Save signature
        </Button>
      </div>
    </div>
  );
}
